import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { adminAPI, providerAdminAPI } from '../../lib/api';
import {
  Search, Calendar, Clock, Users, Phone, Mail, MapPin,
  CheckCircle, XCircle, Edit, Eye, Send, Bell, ChevronDown,
  ChevronUp, FileText, MessageSquare, Trash2, User, AlertCircle,
  Filter, X, SlidersHorizontal, Share2, Copy, ExternalLink, Loader2,
  DollarSign, RefreshCw, CreditCard, Building2, LayoutList, CalendarDays,
  LayoutGrid, Table2, GitCommitHorizontal, ArrowUpDown, ChevronLeft, ChevronRight,
  MessageCircle, Ship, Anchor
} from 'lucide-react';
import BookingCalendarView from './BookingCalendarView';
import MessageTemplateSelector from './MessageTemplateSelector';

const FRONTEND_URL = window.location.origin;

// Format helpers
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price || 0);
};

// Helper function to convert 24hr time to 12hr AM/PM format
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Status badge helper
const getStatusBadge = (booking) => {
  if (booking.status === 'cancelled') return { text: 'Cancelled', class: 'bg-red-100 text-red-700' };
  if (booking.payment_status === 'paid') return { text: 'Paid', class: 'bg-green-100 text-green-700' };
  if (booking.payment_status === 'refunded') return { text: 'Refunded', class: 'bg-purple-100 text-purple-700' };
  return { text: 'Pending', class: 'bg-amber-100 text-amber-700' };
};

// ============ CARD/BOX VIEW COMPONENT ============
function BookingCardsView({ bookings, onSelectBooking, onMarkPaid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
      {bookings.length === 0 ? (
        <div className="col-span-full text-center py-12 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No bookings found</p>
        </div>
      ) : (
        bookings.map((booking) => {
          const status = getStatusBadge(booking);
          const customerName = booking.booker_name || 'Guest';
          const tourDate = booking.date || booking.tour_date;
          // Treat undefined/null is_viewed as already viewed (for legacy bookings)
          const isNewBooking = booking.is_viewed === false;
          const isPending = booking.payment_status !== 'paid';
          
          return (
            <Card 
              key={booking.id}
              className={`p-4 hover:shadow-lg transition-all cursor-pointer border-l-4 ${isNewBooking ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
              style={{ 
                borderLeftColor: booking.status === 'cancelled' ? '#ef4444' : 
                                 booking.payment_status === 'paid' ? '#22c55e' : '#f59e0b'
              }}
              onClick={() => onSelectBooking(booking.id)}
              data-testid={`booking-card-${booking.id}`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <Badge className={status.class}>{status.text}</Badge>
                  {isNewBooking && (
                    <Badge className="bg-blue-500 text-white animate-pulse" data-testid="new-booking-badge">
                      New
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Quick Mark as Paid button */}
                  {isPending && booking.status !== 'cancelled' && (
                    <button
                      onClick={(e) => onMarkPaid(booking, e)}
                      className="p-1.5 rounded-full bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
                      title="Mark as Paid"
                      data-testid={`mark-paid-btn-${booking.id}`}
                    >
                      <DollarSign className="h-4 w-4" />
                    </button>
                  )}
                  <span className="text-lg font-bold text-green-600">
                    {formatPrice(booking.total_price)}
                  </span>
                </div>
              </div>
              
              {/* Tour Title */}
              <h3 className="font-semibold text-sm line-clamp-2 mb-2">
                {booking.tour_title}
              </h3>
              
              {/* Customer */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <User className="h-3.5 w-3.5" />
                <span className="truncate">{customerName}</span>
              </div>
              
              {/* Date & Time */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Calendar className="h-3.5 w-3.5" />
                <span>{tourDate ? new Date(tourDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}</span>
                <Clock className="h-3.5 w-3.5 ml-2" />
                <span>{formatTime12hr(booking.time)}</span>
              </div>
              
              {/* Guests */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                <span>{booking.guests} guest{booking.guests !== 1 ? 's' : ''}</span>
              </div>
              
              {/* Reference */}
              <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                Ref: #{booking.reference_number || booking.id?.slice(0, 8)}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}

// ============ TABLE VIEW COMPONENT ============
function BookingTableView({ bookings, sortConfig, onSort, onSelectBooking }) {
  const sortedBookings = [...bookings].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortConfig.key) {
      case 'date':
        aVal = new Date(a.date || a.tour_date || 0);
        bVal = new Date(b.date || b.tour_date || 0);
        break;
      case 'customer':
        aVal = (a.booker_name || '').toLowerCase();
        bVal = (b.booker_name || '').toLowerCase();
        break;
      case 'tour':
        aVal = (a.tour_title || '').toLowerCase();
        bVal = (b.tour_title || '').toLowerCase();
        break;
      case 'amount':
        aVal = a.total_price || 0;
        bVal = b.total_price || 0;
        break;
      case 'status':
        aVal = a.payment_status || '';
        bVal = b.payment_status || '';
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ label, sortKey }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortConfig.key === sortKey ? 'text-primary' : 'opacity-50'}`} />
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full">
        <thead className="bg-muted/50">
          <tr>
            <SortHeader label="Date" sortKey="date" />
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</th>
            <SortHeader label="Customer" sortKey="customer" />
            <SortHeader label="Tour" sortKey="tour" />
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Guests</th>
            <SortHeader label="Amount" sortKey="amount" />
            <SortHeader label="Status" sortKey="status" />
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Ref</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {sortedBookings.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                No bookings found
              </td>
            </tr>
          ) : (
            sortedBookings.map((booking) => {
              const status = getStatusBadge(booking);
              const tourDate = booking.date || booking.tour_date;
              const isNewBooking = booking.is_viewed === false;
              
              return (
                <tr 
                  key={booking.id}
                  className={`hover:bg-muted/30 cursor-pointer transition-colors ${isNewBooking ? 'bg-blue-50/50' : ''}`}
                  onClick={() => onSelectBooking(booking.id)}
                  data-testid={`booking-row-${booking.id}`}
                >
                  <td className="px-4 py-3 text-sm">
                    {tourDate ? new Date(tourDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm">{formatTime12hr(booking.time)}</td>
                  <td className="px-4 py-3 text-sm font-medium">{booking.booker_name || 'Guest'}</td>
                  <td className="px-4 py-3 text-sm max-w-[200px] truncate">{booking.tour_title}</td>
                  <td className="px-4 py-3 text-sm">{booking.guests}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600">{formatPrice(booking.total_price)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Badge className={`${status.class} text-xs`}>{status.text}</Badge>
                      {isNewBooking && (
                        <Badge className="bg-blue-500 text-white text-xs animate-pulse">New</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    #{booking.reference_number || booking.id?.slice(0, 6)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============ TIMELINE VIEW COMPONENT ============
function BookingTimelineView({ bookings, onSelectBooking }) {
  // Group bookings by date
  const groupedByDate = bookings.reduce((acc, booking) => {
    const date = booking.date || booking.tour_date || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(booking);
    return acc;
  }, {});

  // Sort dates
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isTomorrow = (dateStr) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateStr === tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="space-y-6">
      {sortedDates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No bookings found</p>
        </div>
      ) : (
        sortedDates.map((date) => {
          const dateBookings = groupedByDate[date];
          const formattedDate = date !== 'Unknown' 
            ? new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
            : 'Unknown Date';
          
          return (
            <div key={date} className="relative">
              {/* Date Header */}
              <div className="sticky top-0 z-10 bg-background pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    isToday(date) ? 'bg-red-500' : 
                    isTomorrow(date) ? 'bg-amber-500' : 'bg-primary'
                  }`} />
                  <h3 className="font-semibold text-lg">
                    {isToday(date) ? '🔴 Today' : isTomorrow(date) ? '🟡 Tomorrow' : formattedDate}
                  </h3>
                  <Badge variant="outline">{dateBookings.length} booking{dateBookings.length !== 1 ? 's' : ''}</Badge>
                </div>
              </div>
              
              {/* Timeline Items */}
              <div className="ml-1.5 border-l-2 border-muted pl-6 space-y-4">
                {dateBookings
                  .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                  .map((booking) => {
                    const status = getStatusBadge(booking);
                    const isNewBooking = booking.is_viewed === false;
                    
                    return (
                      <div 
                        key={booking.id}
                        className="relative cursor-pointer"
                        onClick={() => onSelectBooking(booking.id)}
                        data-testid={`timeline-booking-${booking.id}`}
                      >
                        {/* Timeline Dot */}
                        <div className={`absolute -left-[29px] top-2 w-3 h-3 rounded-full bg-background border-2 ${isNewBooking ? 'border-blue-500 animate-pulse' : 'border-primary'}`} />
                        
                        {/* Booking Card */}
                        <Card className={`p-4 hover:shadow-md transition-shadow ${isNewBooking ? 'ring-2 ring-blue-400' : ''}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className="text-lg font-bold text-primary">
                                {formatTime12hr(booking.time)}
                              </div>
                              <div>
                                <h4 className="font-semibold">{booking.tour_title}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {booking.booker_name || 'Guest'} • {booking.guests} guest{booking.guests !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-green-600">{formatPrice(booking.total_price)}</span>
                              <Badge className={status.class}>{status.text}</Badge>
                              {isNewBooking && (
                                <Badge className="bg-blue-500 text-white animate-pulse">New</Badge>
                              )}
                            </div>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

export default function UnifiedBookingsTab({ 
  bookings: initialBookings, 
  tours: propTours, 
  onUpdateStatus, 
  onRefresh, 
  onNavigateToMessaging,
  initialFilter,
  initialViewMode
}) {
  const [bookings, setBookings] = useState(initialBookings || []);
  const [activeView, setActiveView] = useState(initialFilter || 'all');
  const [viewMode, setViewMode] = useState(initialViewMode || 'list'); // 'list', 'calendar', 'cards', 'table', 'timeline'
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [viewingWaiver, setViewingWaiver] = useState(null);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [tours, setTours] = useState(propTours || []);
  const [tableSortConfig, setTableSortConfig] = useState({ key: 'date', direction: 'desc' });
  
  // Notes state
  const [editingNotes, setEditingNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  
  // Waiver sending state
  const [sendingWaiver, setSendingWaiver] = useState({});
  const [waiverSendDialog, setWaiverSendDialog] = useState(null);
  
  // Email composer state (inline like Messaging Center)
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // SMS composer state
  const [smsDialog, setSmsDialog] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  
  // Refund state
  const [refundDialog, setRefundDialog] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundType, setRefundType] = useState('full');
  const [refundReason, setRefundReason] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [sendRefundEmail, setSendRefundEmail] = useState(true);
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // Message Template Selector state
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [templateSelectorChannel, setTemplateSelectorChannel] = useState('email');
  const [templateSelectorData, setTemplateSelectorData] = useState({});
  
  // Helper to open template selector for a booking
  const openTemplateSelector = (channel, booking) => {
    const customerName = booking.booker_name || booking.customer_name || 
      (booking.user_email ? booking.user_email.split('@')[0] : 'Customer');
    const customerEmail = booking.booker_email || booking.user_email;
    const customerPhone = booking.booker_phone || booking.phone;
    
    setTemplateSelectorChannel(channel);
    setTemplateSelectorData({
      customerName,
      customerEmail,
      customerPhone,
      tourTitle: booking.tour_title,
      tourDate: formatDate(booking.date || booking.tour_date),
      tourTime: formatTime12hr(booking.time),
      guests: booking.guests,
      totalPrice: booking.total_price,
      bookingRef: booking.reference_number || booking.id?.slice(0, 8)?.toUpperCase(),
      referenceNumber: booking.reference_number || booking.id?.slice(0, 8)?.toUpperCase(),
      paymentStatus: booking.payment_status || 'Pending',
      pickupLocation: booking.pickup_location || booking.meeting_point || 'Tour meeting point',
      pickupTime: booking.pickup_time || booking.time,
      waiverLink: booking.waiver_link || `${FRONTEND_URL}/waiver/${booking.reference_number}`
    });
    setTemplateSelectorOpen(true);
  };
  
  // Cancellation state
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  const [processingCancel, setProcessingCancel] = useState(false);
  
  // Waiver Reminder state
  const [waiverDialog, setWaiverDialog] = useState(null);
  const [waiverMethod, setWaiverMethod] = useState('email');
  const [waiverCustomMessage, setWaiverCustomMessage] = useState('');
  const [sendingBulkWaiver, setSendingBulkWaiver] = useState(false);
  
  // Forward to Provider state
  const [forwardProviderDialog, setForwardProviderDialog] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [sendingToProvider, setSendingToProvider] = useState(false);
  
  // Assign Provider state
  const [assignProviderDialog, setAssignProviderDialog] = useState(null);
  const [assignProviderId, setAssignProviderId] = useState('');
  const [assignNotifyProvider, setAssignNotifyProvider] = useState(true);
  const [assignSendVia, setAssignSendVia] = useState('email');
  const [assigningProvider, setAssigningProvider] = useState(false);
  
  // Advanced filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterTour, setFilterTour] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');

  useEffect(() => {
    setBookings(initialBookings || []);
  }, [initialBookings]);

  const loadHotels = async () => {
    try {
      const response = await adminAPI.getHotels();
      setHotels(response.data || []);
    } catch (error) {
      console.error('Failed to load hotels:', error);
    }
  };

  const loadTours = async () => {
    try {
      const response = await adminAPI.getAllTours();
      setTours(response.data || []);
    } catch (error) {
      console.error('Failed to load tours:', error);
    }
  };

  const loadProviders = async () => {
    try {
      const response = await adminAPI.getServiceProviders();
      const providersData = response.data?.operators || response.data || [];
      setProviders(Array.isArray(providersData) ? providersData.filter(p => p.is_approved) : []);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  useEffect(() => {
    loadHotels();
    loadProviders();
    if (!propTours?.length) loadTours();
  }, [propTours]);

  // Waiver functions
  const getWaiverUrl = (booking, guest) => {
    if (!booking.reference_number || !guest.waiver_token) return null;
    return `${FRONTEND_URL}/waiver/${booking.reference_number}/${guest.waiver_token}`;
  };

  const copyWaiverLink = (booking, guest) => {
    const url = getWaiverUrl(booking, guest);
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success(`Waiver link copied for ${guest.full_name}`);
    }
  };

  // Send waiver via native device apps (mailto:, sms:, wa.me:)
  const sendWaiverViaNative = (booking, guest, method, contactValue) => {
    const waiverUrl = getWaiverUrl(booking, guest);
    if (!waiverUrl) {
      toast.error('Waiver link not available for this guest');
      return;
    }
    
    const guestName = guest.full_name || 'Guest';
    const tourTitle = booking.tour_title || 'Tour';
    const tourDate = booking.date || booking.tour_date || 'TBD';
    const refNumber = booking.reference_number || 'N/A';
    
    // Build the message
    const message = `Hi ${guestName}!\n\nYou have an upcoming tour "${tourTitle}" on ${tourDate} (Ref: #${refNumber}).\n\nPlease sign your waiver before the tour:\n👉 ${waiverUrl}\n\nThank you!\nTour Finder Puerto Rico`;
    
    switch (method) {
      case 'email':
        const emailSubject = encodeURIComponent(`Sign Your Waiver - ${tourTitle}`);
        const emailBody = encodeURIComponent(message);
        window.location.href = `mailto:${contactValue}?subject=${emailSubject}&body=${emailBody}`;
        toast.success(`Opening email for ${guestName}`);
        break;
        
      case 'sms':
        const smsMessage = `Hi ${guestName}! Please sign your waiver for "${tourTitle}" on ${tourDate}:\n${waiverUrl}`;
        window.location.href = `sms:${contactValue}?body=${encodeURIComponent(smsMessage)}`;
        toast.success(`Opening SMS for ${guestName}`);
        break;
        
      case 'whatsapp':
        const waMessage = `Hi ${guestName}! 👋\n\nYou have an upcoming tour *"${tourTitle}"* on ${tourDate}.\n\n📝 Please sign your waiver:\n${waiverUrl}\n\nThank you!\n*Tour Finder Puerto Rico*`;
        // Remove + from phone number for wa.me link
        const cleanPhone = contactValue.replace(/[^0-9]/g, '');
        window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(waMessage)}`, '_blank');
        toast.success(`Opening WhatsApp for ${guestName}`);
        break;
        
      default:
        copyWaiverLink(booking, guest);
    }
    
    // Close dialog after sending
    setWaiverSendDialog(null);
  };

  // Legacy API-based function (keeping for reference but not using)
  const sendWaiverReminder = async (booking, guestIndex, method, contact = null) => {
    const key = `${booking.id}-${guestIndex}-${method}`;
    setSendingWaiver(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await adminAPI.sendWaiverReminder(booking.id, {
        guest_index: guestIndex,
        method: method,
        phone_number: contact?.phone || null,
        email: contact?.email || null
      });
      
      if (response.data?.success) {
        toast.success(`Waiver reminder sent via ${method}`);
        setWaiverSendDialog(null);
      } else {
        toast.error('Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending waiver reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send waiver reminder');
    } finally {
      setSendingWaiver(prev => ({ ...prev, [key]: false }));
    }
  };

  // Date calculations
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);

  const getBookingDate = (booking) => {
    const dateStr = booking.tour_date || booking.date;
    return dateStr ? new Date(dateStr) : null;
  };

  const isToday = (booking) => {
    const date = getBookingDate(booking);
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (booking) => {
    const date = getBookingDate(booking);
    if (!date) return false;
    return date.toDateString() === tomorrow.toDateString();
  };

  const isThisWeek = (booking) => {
    const date = getBookingDate(booking);
    if (!date) return false;
    return date >= today && date <= nextWeek;
  };

  // Filter bookings
  const filterBookings = (bookingList) => {
    return bookingList.filter(b => {
      const searchLower = searchTerm.toLowerCase();
      
      // Text search filter - includes reference number
      const matchesSearch = searchTerm === '' || 
        b.tour_title?.toLowerCase().includes(searchLower) ||
        b.booker_name?.toLowerCase().includes(searchLower) ||
        b.booker_email?.toLowerCase().includes(searchLower) ||
        b.booker_phone?.includes(searchTerm) ||
        b.user_email?.toLowerCase().includes(searchLower) ||
        b.reference_number?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
      
      // Tour filter
      if (filterTour !== 'all' && b.tour_id !== filterTour) return false;
      
      // Date range filter
      const bookingDate = getBookingDate(b);
      if (filterDateFrom && bookingDate) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (bookingDate < fromDate) return false;
      }
      if (filterDateTo && bookingDate) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        if (bookingDate > toDate) return false;
      }
      
      // Status filter
      if (filterStatus !== 'all' && b.status !== filterStatus) return false;
      
      // Payment status filter
      if (filterPaymentStatus !== 'all' && b.payment_status !== filterPaymentStatus) return false;
      
      return true;
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filterTour !== 'all' || filterDateFrom || filterDateTo || 
                           filterStatus !== 'all' || filterPaymentStatus !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setFilterTour('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterStatus('all');
    setFilterPaymentStatus('all');
    setSearchTerm('');
  };

  // Get unique tours from bookings for filter dropdown
  const uniqueTours = [...new Map(bookings.map(b => [b.tour_id, { id: b.tour_id, title: b.tour_title }])).values()]
    .filter(t => t.id && t.title)
    .sort((a, b) => a.title?.localeCompare(b.title));

  // Sort bookings by created_at (newest first)
  const sortedBookings = [...bookings].sort((a, b) => {
    const dateA = new Date(a.created_at || a.date || 0);
    const dateB = new Date(b.created_at || b.date || 0);
    return dateB - dateA; // Newest first
  });

  const todayBookings = filterBookings(sortedBookings.filter(b => isToday(b) && b.status !== 'cancelled'));
  const tomorrowBookings = filterBookings(sortedBookings.filter(b => isTomorrow(b) && b.status !== 'cancelled'));
  const weekBookings = filterBookings(sortedBookings.filter(b => isThisWeek(b) && b.status !== 'cancelled'));
  const allBookings = filterBookings(sortedBookings);

  const getDisplayBookings = () => {
    switch(activeView) {
      case 'today': return todayBookings;
      case 'tomorrow': return tomorrowBookings;
      case 'week': return weekBookings;
      default: return allBookings;
    }
  };

  const displayBookings = getDisplayBookings();

  // Get customer display name
  const getCustomerName = (booking) => {
    return booking.booker_name || booking.customer_name || booking.user_email?.split('@')[0] || 'Guest';
  };

  const getCustomerEmail = (booking) => {
    return booking.booker_email || booking.user_email || booking.customer_email || '';
  };

  const getCustomerPhone = (booking) => {
    return booking.booker_phone || booking.customer_phone || '';
  };

  // Check if all waivers are signed
  const getWaiverStatus = (booking) => {
    const guests = booking.guest_info || [];
    if (guests.length === 0) return { signed: 0, total: booking.guests || 1, allSigned: false };
    
    const signed = guests.filter(g => g.waiver_signed).length;
    return {
      signed,
      total: guests.length,
      allSigned: signed === guests.length && guests.length > 0
    };
  };

  // Handle booking update
  const handleUpdateBooking = async () => {
    if (!editingBooking) return;
    
    try {
      await adminAPI.updateBooking(editingBooking.id, {
        booker_name: editingBooking.booker_name,
        booker_email: editingBooking.booker_email,
        booker_phone: editingBooking.booker_phone,
        secondary_phone: editingBooking.secondary_phone,
        pickup_hotel: editingBooking.pickup_hotel,
        custom_pickup_location: editingBooking.custom_pickup_location,
        date: editingBooking.date,
        time: editingBooking.time,
        guests: editingBooking.guests,
        special_requests: editingBooking.special_requests,
        guest_info: editingBooking.guest_info
      });
      
      toast.success('Booking updated successfully');
      setEditingBooking(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update booking');
      console.error(error);
    }
  };

  // Mark booking as paid (quick action)
  const handleMarkPaid = async (booking, e) => {
    // Prevent card click event from firing
    if (e) {
      e.stopPropagation();
    }
    
    if (booking.payment_status === 'paid') {
      toast.info('This booking is already marked as paid');
      return;
    }
    
    try {
      await adminAPI.updatePaymentStatus(booking.id, 'paid');
      toast.success(`Marked as paid: ${booking.booker_name || 'Booking'}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to mark as paid');
      console.error(error);
    }
  };

  // Send updated confirmation
  const handleSendConfirmation = async (booking) => {
    setSendingConfirmation(true);
    try {
      await adminAPI.sendCustomerEmail({
        to_email: getCustomerEmail(booking),
        template_type: 'booking_confirmation',
        booking_id: booking.id,
        customer_name: getCustomerName(booking),
        tour_title: booking.tour_title,
        tour_date: booking.date || booking.tour_date,
        tour_time: booking.time,
        guests: booking.guests,
        amount: booking.total_price
      });
      toast.success('Confirmation email sent!');
    } catch (error) {
      toast.error('Failed to send confirmation');
    } finally {
      setSendingConfirmation(false);
    }
  };

  // Open cancel dialog
  const openCancelDialog = (booking) => {
    setCancelDialog(booking);
    setCancelReason('');
    setCancelNotes('');
    setSendCancelEmail(true);
  };

  // Handle cancel booking with notes
  const handleCancelBooking = async () => {
    if (!cancelDialog) return;
    
    if (!cancelReason) {
      toast.error('Please select a cancellation reason');
      return;
    }
    
    setProcessingCancel(true);
    try {
      await adminAPI.cancelBooking(cancelDialog.id, {
        reason: cancelReason,
        admin_notes: cancelNotes,
        send_email: sendCancelEmail
      });
      
      // Update local state
      setBookings(prev => prev.map(b => 
        b.id === cancelDialog.id 
          ? { ...b, status: 'cancelled', cancel_reason: cancelReason, cancel_notes: cancelNotes }
          : b
      ));
      
      toast.success('Booking cancelled successfully');
      setCancelDialog(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to cancel booking');
    } finally {
      setProcessingCancel(false);
    }
  };

  // Handle save notes
  const handleSaveNotes = async (bookingId) => {
    setSavingNotes(prev => ({ ...prev, [bookingId]: true }));
    try {
      await adminAPI.updateBooking(bookingId, {
        admin_notes: editingNotes[bookingId]
      });
      // Update local state
      setBookings(prev => prev.map(b => 
        b.id === bookingId ? { ...b, admin_notes: editingNotes[bookingId] } : b
      ));
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  // Open email composer
  const openEmailComposer = (booking) => {
    const customerEmail = booking.booker_email || booking.user_email || booking.customer_email || booking.email || '';
    const customerName = booking.booker_name || booking.customer_name || 'Customer';
    setEmailDialog({
      booking,
      email: customerEmail,
      name: customerName
    });
    setEmailSubject(`Regarding your booking: ${booking.tour_title}`);
    setEmailBody(`Dear ${customerName},\n\nThank you for booking ${booking.tour_title} on ${formatDate(booking.date)}.\n\n`);
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailDialog || !emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingEmail(true);
    try {
      await adminAPI.sendEmail({
        to_email: emailDialog.email,
        to_name: emailDialog.name,
        subject: emailSubject,
        body: emailBody,
        template_type: 'custom_message',
        booking_id: emailDialog.booking.id
      });
      toast.success('Email sent successfully!');
      setEmailDialog(null);
      setEmailSubject('');
      setEmailBody('');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Open SMS composer
  const openSmsComposer = (booking) => {
    const customerPhone = booking.booker_phone || booking.phone || booking.secondary_phone;
    const customerName = booking.booker_name || booking.customer_name || 'Customer';
    if (!customerPhone) {
      toast.error('No phone number available for this customer');
      return;
    }
    setSmsDialog({
      booking,
      phone: customerPhone,
      name: customerName
    });
    setSmsMessage(`Hi ${customerName}, regarding your ${booking.tour_title} booking on ${formatDate(booking.date)}: `);
  };

  // Send SMS
  const handleSendSms = async () => {
    if (!smsDialog || !smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingSms(true);
    try {
      await adminAPI.sendSms({
        phone_number: smsDialog.phone,
        message: smsMessage,
        booking_id: smsDialog.booking.id
      });
      toast.success('SMS sent successfully!');
      setSmsDialog(null);
      setSmsMessage('');
    } catch (error) {
      toast.error('Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Open refund dialog
  const openRefundDialog = (booking) => {
    setRefundDialog(booking);
    setRefundAmount(booking.total_price?.toString() || '0');
    setRefundType('full');
    setRefundReason('');
    setRefundNotes('');
    setSendRefundEmail(true);
  };

  // Process refund
  const handleProcessRefund = async () => {
    if (!refundDialog) return;
    
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    
    if (amount > refundDialog.total_price) {
      toast.error('Refund amount cannot exceed booking total');
      return;
    }
    
    if (!refundReason.trim()) {
      toast.error('Please select or enter a refund reason');
      return;
    }
    
    setProcessingRefund(true);
    try {
      await adminAPI.processRefund({
        booking_id: refundDialog.id,
        amount: amount,
        reason: refundReason,
        admin_notes: refundNotes,
        send_email: sendRefundEmail,
        refund_type: refundType
      });
      
      // Update local state
      setBookings(prev => prev.map(b => 
        b.id === refundDialog.id 
          ? { ...b, payment_status: amount >= b.total_price ? 'refunded' : 'partial_refund', refund_amount: amount }
          : b
      ));
      
      toast.success(`Refund of ${formatPrice(amount)} processed successfully!`);
      setRefundDialog(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  // Open Send Waiver dialog
  const openWaiverDialog = (booking) => {
    // Count unsigned guests (excluding main booker)
    const guestInfo = booking.guest_info || [];
    const unsignedCount = guestInfo.filter((g, i) => i > 0 && !g.waiver_signed).length;
    
    if (unsignedCount === 0) {
      toast.info('All guests have already signed their waivers');
      return;
    }
    
    setWaiverDialog(booking);
    setWaiverMethod('email');
    setWaiverCustomMessage('');
  };

  // Send Waiver Reminder
  const handleSendWaiverReminder = () => {
    // This function is now a placeholder - the actual send is done via native links
    // in the updated dialog buttons
    if (!waiverDialog) return;
    toast.info('Use the Email, SMS, or WhatsApp buttons below to send the waiver');
  };
  
  // Send waiver via native device apps
  const sendWaiverViaNativeLink = (booking, method) => {
    // Get unsigned guests
    const unsignedGuests = booking.guest_info?.filter((g, i) => i > 0 && !g.waiver_signed) || [];
    if (unsignedGuests.length === 0) {
      toast.error('No unsigned guests found');
      return;
    }
    
    const tourTitle = booking.tour_title || 'Tour';
    const tourDate = formatDate(booking.date || booking.tour_date);
    const refNumber = booking.reference_number || 'N/A';
    const customerName = booking.booker_name || 'Guest';
    const customerEmail = booking.booker_email || booking.customer_email || '';
    const customerPhone = booking.booker_phone || booking.customer_phone || '';
    
    // Build waiver links for each unsigned guest
    let guestLinks = '';
    unsignedGuests.forEach((guest, idx) => {
      const guestName = guest.full_name || `Guest ${idx + 2}`;
      const waiverUrl = guest.waiver_token 
        ? `${FRONTEND_URL}/waiver/${refNumber}/${guest.waiver_token}`
        : 'Link not available';
      guestLinks += `\n• ${guestName}: ${waiverUrl}`;
    });
    
    const customMsg = waiverCustomMessage ? `\n\n${waiverCustomMessage}` : '';
    
    if (method === 'email') {
      const subject = encodeURIComponent(`Sign Your Waiver - ${tourTitle}`);
      const body = encodeURIComponent(`Hi ${customerName},\n\nYou have an upcoming tour "${tourTitle}" on ${tourDate} (Ref: #${refNumber}).\n\nThe following guest(s) still need to sign their waiver:\n${guestLinks}\n\nPlease share the appropriate link with each guest.${customMsg}\n\nThank you!\nTour Finder Puerto Rico`);
      window.location.href = `mailto:${customerEmail}?subject=${subject}&body=${body}`;
      toast.success(`Opening email to ${customerEmail}`);
    } else if (method === 'sms') {
      const smsBody = encodeURIComponent(`Hi ${customerName}! Waiver reminder for "${tourTitle}" on ${tourDate}.\n\nGuests needing to sign:${guestLinks}\n\nPlease sign before your tour!`);
      window.location.href = `sms:${customerPhone}?body=${smsBody}`;
      toast.success(`Opening SMS to ${customerPhone}`);
    } else if (method === 'whatsapp') {
      const waBody = encodeURIComponent(`Hi ${customerName}! 👋\n\nThis is a reminder for your upcoming tour *"${tourTitle}"* on ${tourDate} (Ref: #${refNumber}).\n\n📝 The following guest(s) still need to sign their waiver:\n${guestLinks}\n\nPlease share the links with each guest and sign before your tour!${customMsg}\n\nThank you!\n*Tour Finder Puerto Rico*`);
      const cleanPhone = customerPhone.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${cleanPhone}?text=${waBody}`, '_blank');
      toast.success(`Opening WhatsApp to ${customerPhone}`);
    }
    
    setWaiverDialog(null);
  };

  // Get unsigned guests count for a booking
  const getUnsignedGuestsCount = (booking) => {
    const guestInfo = booking.guest_info || [];
    return guestInfo.filter((g, i) => i > 0 && !g.waiver_signed).length;
  };

  // Handle Share Booking
  const handleShareBooking = (booking) => {
    const customerName = booking.booker_name || booking.customer_name || 'Customer';
    const tourTitle = booking.tour_title || 'Tour';
    const dateStr = formatDate(booking.date || booking.tour_date);
    const timeStr = formatTime12hr(booking.time);
    
    const shareText = `Booking Confirmation\n\nCustomer: ${customerName}\nTour: ${tourTitle}\nDate: ${dateStr}\nTime: ${timeStr}\nGuests: ${booking.guests}\nTotal: ${formatPrice(booking.total_price)}\nReference: #${booking.reference_number || 'N/A'}`;
    
    // Try native share API first
    if (navigator.share) {
      navigator.share({
        title: `Booking: ${tourTitle}`,
        text: shareText
      }).catch(() => {
        // Fall back to clipboard
        navigator.clipboard.writeText(shareText);
        toast.success('Booking details copied to clipboard!');
      });
    } else {
      // Fall back to clipboard
      navigator.clipboard.writeText(shareText);
      toast.success('Booking details copied to clipboard!');
    }
  };

  // Handle Delete Booking
  const handleDeleteBooking = async (booking) => {
    const customerName = booking.booker_name || booking.customer_name || 'this booking';
    
    if (!window.confirm(`Are you sure you want to permanently delete the booking for ${customerName}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      await adminAPI.deleteBooking(booking.id);
      
      // Update local state
      setBookings(prev => prev.filter(b => b.id !== booking.id));
      
      toast.success('Booking deleted successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  // Handle Forward Booking to Provider
  const handleForwardToProvider = async () => {
    if (!forwardProviderDialog || !selectedProviderId) {
      toast.error('Please select a provider');
      return;
    }
    
    const booking = forwardProviderDialog;
    const provider = providers.find(p => p.id === selectedProviderId);
    
    if (!provider?.email) {
      toast.error('Selected provider has no email address');
      return;
    }
    
    setSendingToProvider(true);
    try {
      const customerName = booking.booker_name || booking.customer_name || 'Customer';
      const customerEmail = booking.booker_email || booking.customer_email || '';
      const customerPhone = booking.booker_phone || booking.customer_phone || 'N/A';
      const tourTitle = booking.tour_title || 'Tour';
      const dateStr = formatDate(booking.date || booking.tour_date);
      const timeStr = formatTime12hr(booking.time);
      const pickupLocation = booking.pickup_hotel || booking.custom_pickup_location || 'Not specified';
      const specialRequests = booking.special_requests || 'None';
      
      const emailSubject = `New Booking Assignment: ${tourTitle} - ${dateStr}`;
      const emailBody = `Hi ${provider.name || provider.company_name},

You have been assigned a new booking. Please see the details below:

BOOKING DETAILS
---------------
Tour: ${tourTitle}
Date: ${dateStr}
Time: ${timeStr}
Number of Guests: ${booking.guests}
Reference: #${booking.reference_number || 'N/A'}

CUSTOMER INFORMATION
--------------------
Name: ${customerName}
Email: ${customerEmail}
Phone: ${customerPhone}
Pickup Location: ${pickupLocation}
Special Requests: ${specialRequests}

${booking.guest_info?.length > 1 ? `GUEST LIST
----------
${booking.guest_info.map((g, i) => `${i + 1}. ${g.full_name || 'Guest'}`).join('\n')}` : ''}

Please confirm you have received this booking information.

Best regards,
Tour Finder Admin`;

      await adminAPI.sendCustomerEmail({
        to_email: provider.email,
        subject: emailSubject,
        body: emailBody,
        customer_name: provider.name || provider.company_name
      });
      
      toast.success(`Booking forwarded to ${provider.name || provider.company_name}!`);
      setForwardProviderDialog(null);
      setSelectedProviderId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to forward booking');
    } finally {
      setSendingToProvider(false);
    }
  };

  // Handle Assign Provider to Booking
  const handleAssignProvider = async () => {
    if (!assignProviderDialog || !assignProviderId) {
      toast.error('Please select a provider');
      return;
    }
    
    setAssigningProvider(true);
    try {
      const res = await providerAdminAPI.assignBooking(assignProviderDialog.id, {
        provider_id: assignProviderId,
        notify_provider: assignNotifyProvider,
        send_via: assignSendVia
      });
      
      // Update local state
      const provider = providers.find(p => p.id === assignProviderId);
      setBookings(prev => prev.map(b => 
        b.id === assignProviderDialog.id 
          ? { 
              ...b, 
              assigned_provider_id: assignProviderId, 
              assigned_provider_name: provider?.name,
              provider_earnings: res.data.provider_earnings
            } 
          : b
      ));
      
      toast.success(res.data.message || `Booking assigned to ${provider?.name}!`);
      setAssignProviderDialog(null);
      setAssignProviderId('');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign provider');
    } finally {
      setAssigningProvider(false);
    }
  };

  // Handle Unassign Provider
  const handleUnassignProvider = async (booking) => {
    if (!window.confirm('Remove provider assignment from this booking?')) return;
    
    try {
      await providerAdminAPI.unassignBooking(booking.id);
      
      setBookings(prev => prev.map(b => 
        b.id === booking.id 
          ? { ...b, assigned_provider_id: null, assigned_provider_name: null, provider_earnings: null } 
          : b
      ));
      
      toast.success('Provider assignment removed');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to unassign provider');
    }
  };

  // Toggle expanded booking and mark as viewed
  const toggleExpanded = async (bookingId) => {
    const newExpandedId = expandedBooking === bookingId ? null : bookingId;
    setExpandedBooking(newExpandedId);
    
    // Mark as viewed when expanding (not collapsing)
    if (newExpandedId) {
      const booking = bookings.find(b => b.id === bookingId);
      if (booking && booking.is_viewed === false) {
        try {
          await adminAPI.markBookingViewed(bookingId);
          // Update local state to reflect the change
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Failed to mark booking as viewed:', error);
        }
      }
    }
  };

  // Select a booking from other views (cards, table, timeline, calendar)
  const handleSelectBooking = async (bookingId) => {
    setViewMode('list');
    setExpandedBooking(bookingId);
    
    // Mark as viewed when selecting
    const booking = bookings.find(b => b.id === bookingId);
    if (booking && booking.is_viewed === false) {
      try {
        await adminAPI.markBookingViewed(bookingId);
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error('Failed to mark booking as viewed:', error);
      }
    }
  };

  return (
    <div data-testid="unified-bookings-tab" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Bookings</h1>
          <p className="text-muted-foreground">Manage all bookings, customers, and waivers in one place</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-1.5 rounded-none px-3"
              data-testid="view-list-btn"
              title="List View"
            >
              <LayoutList className="h-4 w-4" />
              <span className="hidden sm:inline">List</span>
            </Button>
            <Button 
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="gap-1.5 rounded-none px-3"
              data-testid="view-cards-btn"
              title="Card View"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Cards</span>
            </Button>
            <Button 
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="gap-1.5 rounded-none px-3"
              data-testid="view-table-btn"
              title="Table View"
            >
              <Table2 className="h-4 w-4" />
              <span className="hidden sm:inline">Table</span>
            </Button>
            <Button 
              variant={viewMode === 'timeline' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('timeline')}
              className="gap-1.5 rounded-none px-3"
              data-testid="view-timeline-btn"
              title="Timeline View"
            >
              <GitCommitHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Timeline</span>
            </Button>
            <Button 
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="gap-1.5 rounded-none px-3"
              data-testid="view-calendar-btn"
              title="Calendar View"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Calendar</span>
            </Button>
          </div>
          <Button onClick={onRefresh} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' ? (
        <BookingCalendarView 
          bookings={bookings} 
          onRefresh={onRefresh}
          onNavigateToBooking={handleSelectBooking}
        />
      ) : viewMode === 'cards' ? (
        <BookingCardsView 
          bookings={displayBookings}
          onSelectBooking={handleSelectBooking}
          onMarkPaid={handleMarkPaid}
        />
      ) : viewMode === 'table' ? (
        <BookingTableView 
          bookings={displayBookings}
          sortConfig={tableSortConfig}
          onSort={(key) => {
            setTableSortConfig(prev => ({
              key,
              direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
          }}
          onSelectBooking={handleSelectBooking}
        />
      ) : viewMode === 'timeline' ? (
        <BookingTimelineView 
          bookings={displayBookings}
          onSelectBooking={handleSelectBooking}
        />
      ) : (
        <>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card 
              className={`p-4 cursor-pointer transition-all ${activeView === 'today' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
              onClick={() => setActiveView('today')}
              data-testid="stats-today"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Bell className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{todayBookings.length}</p>
              <p className="text-sm text-muted-foreground">Today</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'tomorrow' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('tomorrow')}
          data-testid="stats-tomorrow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tomorrowBookings.length}</p>
              <p className="text-sm text-muted-foreground">Tomorrow</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'week' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('week')}
          data-testid="stats-week"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{weekBookings.length}</p>
              <p className="text-sm text-muted-foreground">This Week</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer transition-all ${activeView === 'all' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setActiveView('all')}
          data-testid="stats-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{bookings.length}</p>
              <p className="text-sm text-muted-foreground">All Bookings</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="p-4 rounded-xl">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone, reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="booking-search"
            />
          </div>

          {/* Filter Toggle Button */}
          <Button
            variant={showFilters || hasActiveFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
            data-testid="filter-toggle"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-1 bg-white text-primary h-5 w-5 p-0 flex items-center justify-center text-xs">
                !
              </Badge>
            )}
          </Button>

          {/* Clear Filters */}
          {(hasActiveFilters || searchTerm) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground">
              <X className="h-4 w-4" />
              Clear All
            </Button>
          )}
        </div>

        {/* Expanded Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Tour Filter */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Tour</Label>
              <Select value={filterTour} onValueChange={setFilterTour}>
                <SelectTrigger data-testid="filter-tour">
                  <SelectValue placeholder="All Tours" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tours</SelectItem>
                  {uniqueTours.map(tour => (
                    <SelectItem key={tour.id} value={tour.id}>{tour.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">From Date</Label>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                data-testid="filter-date-from"
              />
            </div>

            {/* Date To */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">To Date</Label>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                data-testid="filter-date-to"
              />
            </div>

            {/* Booking Status */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Booking Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment Status */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Payment Status</Label>
              <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
                <SelectTrigger data-testid="filter-payment-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
          <span>
            Showing <span className="font-semibold text-foreground">{displayBookings.length}</span> of {bookings.length} bookings
          </span>
          {hasActiveFilters && (
            <Badge variant="secondary" className="text-xs">Filtered</Badge>
          )}
        </div>
      </Card>

      {/* Bookings List */}
      {displayBookings.length === 0 ? (
        <Card className="p-12 text-center rounded-xl">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">No Bookings Found</h3>
          <p className="text-muted-foreground">
            {searchTerm ? 'No bookings match your search' : 'Bookings will appear here'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {displayBookings.map((booking) => {
            const bookingIsToday = isToday(booking);
            const bookingIsTomorrow = isTomorrow(booking);
            const isExpanded = expandedBooking === booking.id;
            const waiverStatus = getWaiverStatus(booking);
            const customerName = getCustomerName(booking);
            const customerEmail = getCustomerEmail(booking);
            const customerPhone = getCustomerPhone(booking);

            return (
              <Card 
                key={booking.id}
                className={`rounded-xl overflow-hidden transition-all ${
                  bookingIsToday ? 'border-l-4 border-l-red-500 bg-red-50/30' : 
                  bookingIsTomorrow ? 'border-l-4 border-l-amber-500 bg-amber-50/30' : ''
                }`}
                data-testid={`booking-card-${booking.id}`}
              >
                {/* Booking Header - Always Visible */}
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleExpanded(booking.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Tour & Customer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-bold text-xl text-gray-900">{booking.tour_title}</h3>
                        {booking.reference_number && (
                          <Badge variant="outline" className="font-mono text-sm font-bold">
                            #{booking.reference_number}
                          </Badge>
                        )}
                        {bookingIsToday && <Badge className="bg-red-500 text-white font-bold">Today</Badge>}
                        {bookingIsTomorrow && <Badge className="bg-amber-500 text-white font-bold">Tomorrow</Badge>}
                        {/* Waiver Status Badge */}
                        {booking.all_waivers_signed ? (
                          <Badge className="bg-green-100 text-green-700 gap-1 font-semibold">
                            <CheckCircle className="h-3 w-3" />
                            Waivers Signed
                          </Badge>
                        ) : booking.guest_info?.length > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700 gap-1 font-semibold">
                            <AlertCircle className="h-3 w-3" />
                            {booking.guest_info.filter(g => g.waiver_signed).length}/{booking.guest_info.length} Waivers
                          </Badge>
                        ) : null}
                        <Badge className={`font-semibold ${booking.status === 'confirmed' ? 'bg-green-100 text-green-700' : booking.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {booking.status}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Customer */}
                        <div className="flex items-center gap-2">
                          <User className="h-5 w-5 text-muted-foreground" />
                          <span className="font-bold text-xl text-gray-900">{customerName}</span>
                        </div>
                        
                        {/* Date & Time */}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                          <span className="font-bold text-lg text-gray-900">{formatDate(booking.date || booking.tour_date)}</span>
                          <span className="text-muted-foreground font-medium">at</span>
                          <span className="font-bold text-lg text-gray-900">{formatTime12hr(booking.time)}</span>
                        </div>
                        
                        {/* Guests & Waivers */}
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-muted-foreground" />
                          <span className="font-bold text-xl text-gray-900">{booking.guests} guest{booking.guests !== 1 ? 's' : ''}</span>
                          {waiverStatus.allSigned ? (
                            <Badge className="bg-green-100 text-green-700 text-xs font-semibold">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Waivers Signed
                            </Badge>
                          ) : waiverStatus.signed > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs font-semibold">
                              {waiverStatus.signed}/{waiverStatus.total} Signed
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600 text-xs font-semibold">
                              No Waivers
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Price & Expand */}
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-xl text-green-600">{formatPrice(booking.total_price)}</p>
                        <Badge className={`text-xs font-bold ${booking.payment_status === 'paid' ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'}`}>
                          {booking.payment_status?.toUpperCase() || 'PENDING'}
                        </Badge>
                      </div>
                      <Button variant="ghost" size="sm">
                        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t bg-muted/20 p-4 space-y-4">
                    {/* Contact Actions - Template-Based Messaging */}
                    <div className="flex flex-wrap gap-2 pb-4 border-b">
                      {customerPhone && (
                        <>
                          {/* Call - Opens phone dialer */}
                          <a href={`tel:${customerPhone.replace(/[\s()-]/g, '')}`}>
                            <Button variant="outline" size="sm" className="gap-2" data-testid={`call-btn-${booking.id}`}>
                              <Phone className="h-4 w-4" />
                              Call
                            </Button>
                          </a>
                          {/* SMS - Opens Template Selector */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-2" 
                            data-testid={`sms-btn-${booking.id}`}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              openTemplateSelector('sms', booking); 
                            }}
                          >
                            <MessageSquare className="h-4 w-4" />
                            Text
                          </Button>
                          {/* WhatsApp - Opens Template Selector */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-2 text-green-600 hover:text-green-700 hover:bg-green-50" 
                            data-testid={`whatsapp-btn-${booking.id}`}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              openTemplateSelector('whatsapp', booking); 
                            }}
                          >
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp
                          </Button>
                          {/* Copy Phone */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="gap-1 text-muted-foreground"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              navigator.clipboard.writeText(customerPhone);
                              toast.success('Phone copied!');
                            }}
                          >
                            <Copy className="h-3 w-3" />
                            {customerPhone}
                          </Button>
                        </>
                      )}
                      {customerEmail && (
                        <>
                          {/* Email - Opens Template Selector */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-2" 
                            data-testid={`email-btn-${booking.id}`}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              openTemplateSelector('email', booking); 
                            }}
                          >
                            <Mail className="h-4 w-4" />
                            Email
                          </Button>
                          {/* Copy Email */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="gap-1 text-muted-foreground"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              navigator.clipboard.writeText(customerEmail);
                              toast.success('Email copied!');
                            }}
                          >
                            <Copy className="h-3 w-3" />
                            {customerEmail}
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2"
                        onClick={() => setEditingBooking({
                          ...booking,
                          // Normalize email field - check multiple possible field names
                          booker_email: booking.booker_email || booking.user_email || booking.customer_email || booking.email || ''
                        })}
                        data-testid={`edit-btn-${booking.id}`}
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      {booking.status !== 'cancelled' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); openCancelDialog(booking); }}
                          data-testid={`cancel-btn-${booking.id}`}
                        >
                          <XCircle className="h-4 w-4" />
                          Cancel
                        </Button>
                      )}
                      {customerPhone && (
                        <a href={`https://wa.me/${customerPhone.replace(/\D/g, '')}?text=Hi ${customerName || 'there'}! This is Tour Finder regarding your booking.`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-2 text-green-600 hover:text-green-700" data-testid={`whatsapp-btn-${booking.id}`}>
                            <MessageSquare className="h-4 w-4" />
                            WhatsApp
                          </Button>
                        </a>
                      )}
                      {/* Refund Button - Only show for paid bookings */}
                      {booking.payment_status === 'paid' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={(e) => { e.stopPropagation(); openRefundDialog(booking); }}
                          data-testid={`refund-btn-${booking.id}`}
                        >
                          <RefreshCw className="h-4 w-4" />
                          Refund
                        </Button>
                      )}
                      {/* Send Waiver Button - Show if there are unsigned guests */}
                      {getUnsignedGuestsCount(booking) > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          onClick={(e) => { e.stopPropagation(); openWaiverDialog(booking); }}
                          data-testid={`send-waiver-btn-${booking.id}`}
                        >
                          <FileText className="h-4 w-4" />
                          Send Waiver ({getUnsignedGuestsCount(booking)})
                        </Button>
                      )}
                      {/* Share Booking Button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2"
                        onClick={(e) => { e.stopPropagation(); handleShareBooking(booking); }}
                        data-testid={`share-btn-${booking.id}`}
                      >
                        <Share2 className="h-4 w-4" />
                        Share
                      </Button>
                      {/* Delete Booking Button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDeleteBooking(booking); }}
                        data-testid={`delete-btn-${booking.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                      
                      {/* Assign Provider Button */}
                      {booking.assigned_provider_id ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700 gap-1">
                            <Building2 className="h-3 w-3" />
                            {booking.assigned_provider_name || 'Provider Assigned'}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                            onClick={(e) => { e.stopPropagation(); handleUnassignProvider(booking); }}
                            title="Remove assignment"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setAssignProviderDialog(booking);
                            setAssignProviderId('');
                            setAssignNotifyProvider(true);
                            setAssignSendVia('email');
                          }}
                          data-testid={`assign-provider-btn-${booking.id}`}
                        >
                          <Building2 className="h-4 w-4" />
                          Assign Provider
                        </Button>
                      )}
                      {/* Forward to Provider Button */}
                      {providers.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          onClick={(e) => { e.stopPropagation(); setForwardProviderDialog(booking); }}
                          data-testid={`forward-provider-btn-${booking.id}`}
                        >
                          <Users className="h-4 w-4" />
                          Forward to Provider
                        </Button>
                      )}
                      {booking.payment_status === 'refunded' && (
                        <Badge className="bg-purple-100 text-purple-700">
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Refunded
                        </Badge>
                      )}
                      {booking.payment_status === 'partial_refund' && (
                        <Badge className="bg-purple-100 text-purple-700">
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Partial Refund ({formatPrice(booking.refund_amount)})
                        </Badge>
                      )}
                    </div>

                    {/* Customer & Booking Details */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Customer Info */}
                      <div className="bg-white p-4 rounded-lg border">
                        <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900">
                          <User className="h-5 w-5" />
                          Customer Information
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Name:</span>
                            <span className="font-bold text-lg text-gray-900">{customerName}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Email:</span>
                            <span className="font-semibold text-base text-gray-900">{customerEmail || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Phone:</span>
                            <span className="font-bold text-base text-gray-900">{customerPhone || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Secondary Phone:</span>
                            <span className="font-semibold text-base text-gray-900">{booking.secondary_phone || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Pickup:</span>
                            <span className="font-semibold text-base text-gray-900">{booking.pickup_hotel || booking.custom_pickup_location || 'Not specified'}</span>
                          </div>
                        </div>
                        
                        {/* Quick Contact Actions - Template-Based */}
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Quick Contact (choose template):</p>
                          <div className="flex flex-wrap gap-2">
                            {/* Email with template selector */}
                            {customerEmail && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="gap-1"
                                data-testid={`customer-email-btn-${booking.id}`}
                                onClick={() => openTemplateSelector('email', booking)}
                              >
                                <Mail className="h-3 w-3" /> Email
                              </Button>
                            )}
                            
                            {/* SMS with template selector */}
                            {customerPhone && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="gap-1"
                                data-testid={`customer-sms-btn-${booking.id}`}
                                onClick={() => openTemplateSelector('sms', booking)}
                              >
                                <MessageSquare className="h-3 w-3" /> Text
                              </Button>
                            )}
                            
                            {/* WhatsApp with template selector */}
                            {customerPhone && (
                              <Button 
                                size="sm" 
                                className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`customer-whatsapp-btn-${booking.id}`}
                                onClick={() => openTemplateSelector('whatsapp', booking)}
                              >
                                <MessageCircle className="h-3 w-3" /> WhatsApp
                              </Button>
                            )}
                            
                            {/* Call button */}
                            {customerPhone && (
                              <a 
                                href={`tel:${customerPhone.replace(/[\s()-]/g, '')}`}
                                data-testid={`customer-call-btn-${booking.id}`}
                              >
                                <Button size="sm" variant="outline" className="gap-1">
                                  <Phone className="h-3 w-3" /> Call
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Booking Details */}
                      <div className="bg-white p-4 rounded-lg border">
                        <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900">
                          <Calendar className="h-5 w-5" />
                          Booking Details
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Tour:</span>
                            <span className="font-bold text-base text-gray-900">{booking.tour_title}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Date:</span>
                            <span className="font-bold text-lg text-gray-900">{formatDate(booking.date || booking.tour_date)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Time:</span>
                            <span className="font-bold text-lg text-gray-900">{formatTime12hr(booking.time)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Guests:</span>
                            <span className="font-bold text-lg text-gray-900">{booking.guests}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Total:</span>
                            <span className="font-bold text-xl text-green-600">{formatPrice(booking.total_price)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Payment:</span>
                            <div className="flex items-center gap-2">
                              <Badge className={booking.payment_status === 'paid' ? 'bg-green-500 text-white font-bold' : 'bg-amber-500 text-white font-bold'}>
                                {booking.payment_status?.toUpperCase() || 'PENDING'}
                              </Badge>
                              {booking.payment_status !== 'paid' && booking.status !== 'cancelled' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                                  onClick={() => handleMarkPaid(booking)}
                                  data-testid={`detail-mark-paid-${booking.id}`}
                                >
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  Mark Paid
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Cruise Traveler Info */}
                          {booking.is_cruise_traveler && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-2 text-blue-700 font-semibold mb-2">
                                <Ship className="h-5 w-5" />
                                Cruise Traveler
                              </div>
                              {booking.cruise_line && (
                                <div className="flex items-center gap-2 text-blue-600">
                                  <Anchor className="h-4 w-4" />
                                  <span className="font-medium">
                                    {booking.cruise_line === 'carnival' ? 'Carnival Cruise Line' :
                                     booking.cruise_line === 'disney' ? 'Disney Cruise Line' :
                                     booking.cruise_line === 'royal' ? 'Royal Caribbean' :
                                     booking.cruise_line === 'norwegian' ? 'Norwegian Cruise Line' :
                                     booking.cruise_line === 'celebrity' ? 'Celebrity Cruises' :
                                     booking.cruise_line === 'msc' ? 'MSC Cruises' :
                                     booking.cruise_line === 'virgin' ? 'Virgin Voyages' :
                                     booking.cruise_line === 'holland' ? 'Holland America Line' :
                                     booking.cruise_line === 'not_listed' ? 'Other / Not Listed' :
                                     booking.cruise_line}
                                  </span>
                                </div>
                              )}
                              <p className="text-xs text-blue-500 mt-1">Pickup at cruise port</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Special Requests */}
                    {booking.special_requests && (
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold mb-2">Special Requests</h4>
                        <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                          {booking.special_requests}
                        </p>
                      </div>
                    )}

                    {/* Admin Notes */}
                    <div className="pt-4 border-t">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Admin Notes
                      </h4>
                      <Textarea
                        placeholder="Add notes about this booking (only visible to admin)..."
                        value={editingNotes[booking.id] !== undefined ? editingNotes[booking.id] : (booking.admin_notes || '')}
                        onChange={(e) => setEditingNotes(prev => ({ ...prev, [booking.id]: e.target.value }))}
                        className="min-h-[80px]"
                        data-testid={`notes-${booking.id}`}
                      />
                      <Button 
                        size="sm" 
                        className="mt-2"
                        onClick={() => handleSaveNotes(booking.id)}
                        disabled={savingNotes[booking.id]}
                      >
                        {savingNotes[booking.id] ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
                        ) : (
                          'Save Notes'
                        )}
                      </Button>
                    </div>

                    {/* Guest List & Waivers */}
                    <div className="pt-4 border-t">
                      <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900">
                        <FileText className="h-5 w-5" />
                        Guest List & Waivers
                      </h4>
                      
                      {(booking.guest_info && booking.guest_info.length > 0) ? (
                        <div className="space-y-3">
                          {booking.guest_info.map((guest, index) => (
                            <div 
                              key={index} 
                              className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                                guest.waiver_signed 
                                  ? 'bg-green-50 border-green-200' 
                                  : 'bg-amber-50 border-amber-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  guest.waiver_signed ? 'bg-green-200' : 'bg-amber-200'
                                }`}>
                                  {guest.waiver_signed ? (
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                  ) : (
                                    <AlertCircle className="h-5 w-5 text-amber-500" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-bold text-lg text-gray-900">{guest.full_name}</p>
                                  <p className="text-sm text-gray-600">
                                    {guest.email || guest.phone || `Guest ${index + 1}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {guest.waiver_signed ? (
                                  <>
                                    <Badge className="bg-green-500 text-white font-bold text-sm px-3 py-1">✓ SIGNED</Badge>
                                    {guest.signature_data && (
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setViewingWaiver({ guest, booking })}
                                        data-testid={`view-waiver-${index}`}
                                      >
                                        <Eye className="h-4 w-4 mr-1" />
                                        View
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Badge className="bg-amber-500 text-white font-bold text-sm px-3 py-1">⏳ PENDING</Badge>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => copyWaiverLink(booking, guest)}
                                      title="Copy waiver link"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="default" 
                                      size="sm"
                                      onClick={() => setWaiverSendDialog({ booking, guest, guestIndex: index })}
                                      data-testid={`send-waiver-${index}`}
                                    >
                                      <Send className="h-4 w-4 mr-1" />
                                      Send Waiver
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-base text-gray-600">
                          No guest information available for this booking.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Booking Dialog */}
      <Dialog open={!!editingBooking} onOpenChange={() => setEditingBooking(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
          </DialogHeader>
          
          {editingBooking && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={editingBooking.booker_name || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, booker_name: e.target.value})}
                    data-testid="edit-booker-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingBooking.booker_email || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, booker_email: e.target.value})}
                    data-testid="edit-booker-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editingBooking.booker_phone || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, booker_phone: e.target.value})}
                    data-testid="edit-booker-phone"
                  />
                </div>
                <div>
                  <Label>Secondary Phone (Optional)</Label>
                  <Input
                    value={editingBooking.secondary_phone || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, secondary_phone: e.target.value})}
                    data-testid="edit-secondary-phone"
                    placeholder="Alternative contact number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Location</Label>
                  <Select 
                    value={editingBooking.pickup_hotel || 'none'} 
                    onValueChange={(val) => setEditingBooking({...editingBooking, pickup_hotel: val === 'none' ? '' : val})}
                  >
                    <SelectTrigger data-testid="edit-pickup-hotel">
                      <SelectValue placeholder="Select pickup location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No pickup needed</SelectItem>
                      {hotels.map((hotel) => (
                        <SelectItem key={hotel.id} value={hotel.name}>
                          {hotel.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Custom Pickup Location</Label>
                  <Input
                    value={editingBooking.custom_pickup_location || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, custom_pickup_location: e.target.value})}
                    data-testid="edit-custom-pickup"
                    placeholder="If hotel not in list"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={editingBooking.date || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, date: e.target.value})}
                    data-testid="edit-date"
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={editingBooking.time || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, time: e.target.value})}
                    data-testid="edit-time"
                  />
                </div>
                <div>
                  <Label>Guests</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editingBooking.guests || 1}
                    onChange={(e) => setEditingBooking({...editingBooking, guests: parseInt(e.target.value)})}
                    data-testid="edit-guests"
                  />
                </div>
              </div>

              <div>
                <Label>Special Requests</Label>
                <Textarea
                  value={editingBooking.special_requests || ''}
                  onChange={(e) => setEditingBooking({...editingBooking, special_requests: e.target.value})}
                  rows={3}
                  data-testid="edit-special-requests"
                />
              </div>

              {/* Edit Guest Info */}
              {editingBooking.guest_info && editingBooking.guest_info.length > 0 && (
                <div>
                  <Label className="mb-2 block">Guest Information</Label>
                  {editingBooking.guest_info.map((guest, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        placeholder="Full Name"
                        value={guest.full_name || ''}
                        onChange={(e) => {
                          const updated = [...editingBooking.guest_info];
                          updated[index] = {...updated[index], full_name: e.target.value};
                          setEditingBooking({...editingBooking, guest_info: updated});
                        }}
                        className="flex-1"
                      />
                      <Input
                        type="date"
                        placeholder="DOB"
                        value={guest.date_of_birth || ''}
                        onChange={(e) => {
                          const updated = [...editingBooking.guest_info];
                          updated[index] = {...updated[index], date_of_birth: e.target.value};
                          setEditingBooking({...editingBooking, guest_info: updated});
                        }}
                        className="w-40"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingBooking(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateBooking}
              data-testid="save-booking-btn"
            >
              Save Changes
            </Button>
            <Button 
              variant="secondary"
              onClick={() => {
                handleUpdateBooking();
                if (editingBooking) handleSendConfirmation(editingBooking);
              }}
              data-testid="save-and-send-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Save & Send Confirmation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Waiver Dialog */}
      <Dialog open={!!viewingWaiver} onOpenChange={() => setViewingWaiver(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Waiver Signature</DialogTitle>
          </DialogHeader>
          
          {viewingWaiver && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Guest Name:</span>
                  <span className="font-medium">{viewingWaiver.guest.full_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date of Birth:</span>
                  <span>{viewingWaiver.guest.date_of_birth || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tour:</span>
                  <span>{viewingWaiver.booking.tour_title}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tour Date:</span>
                  <span>{formatDate(viewingWaiver.booking.date || viewingWaiver.booking.tour_date)}</span>
                </div>
                {viewingWaiver.guest.signed_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Signed At:</span>
                    <span>{new Date(viewingWaiver.guest.signed_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Signature Display */}
              {viewingWaiver.guest.signature_data && (
                <div className="border rounded-lg p-4 bg-white">
                  <Label className="text-sm text-muted-foreground mb-2 block">Signature:</Label>
                  <img 
                    src={viewingWaiver.guest.signature_data} 
                    alt="Signature" 
                    className="max-w-full h-auto border rounded"
                  />
                </div>
              )}

              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-700 font-medium">Waiver has been signed</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setViewingWaiver(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Waiver Dialog */}
      <Dialog open={!!waiverSendDialog} onOpenChange={() => setWaiverSendDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Waiver Reminder
            </DialogTitle>
          </DialogHeader>
          
          {waiverSendDialog && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium">{waiverSendDialog.guest.full_name}</p>
                <p className="text-sm text-muted-foreground">
                  {waiverSendDialog.booking.tour_title}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Reference: {waiverSendDialog.booking.reference_number}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Waiver Link</Label>
                <div className="flex gap-2">
                  <Input 
                    value={getWaiverUrl(waiverSendDialog.booking, waiverSendDialog.guest) || ''} 
                    readOnly 
                    className="text-xs"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyWaiverLink(waiverSendDialog.booking, waiverSendDialog.guest)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Send via:</Label>
                
                {/* Email Option */}
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="Email address" 
                    defaultValue={waiverSendDialog.guest.email || waiverSendDialog.booking.booker_email || ''}
                    id="waiver-email"
                    type="email"
                    className="flex-1"
                  />
                  <Button
                    onClick={() => {
                      const email = document.getElementById('waiver-email').value;
                      if (!email) {
                        toast.error('Please enter an email address');
                        return;
                      }
                      sendWaiverViaNative(waiverSendDialog.booking, waiverSendDialog.guest, 'email', email);
                    }}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                </div>

                {/* SMS Option */}
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="Phone number" 
                    defaultValue={waiverSendDialog.guest.phone || waiverSendDialog.booking.booker_phone || ''}
                    id="waiver-phone"
                    type="tel"
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      const phone = document.getElementById('waiver-phone').value;
                      if (!phone) {
                        toast.error('Please enter a phone number');
                        return;
                      }
                      sendWaiverViaNative(waiverSendDialog.booking, waiverSendDialog.guest, 'sms', phone);
                    }}
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    SMS
                  </Button>
                </div>

                {/* WhatsApp Option */}
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="WhatsApp number" 
                    defaultValue={waiverSendDialog.guest.phone || waiverSendDialog.booking.booker_phone || ''}
                    id="waiver-whatsapp"
                    type="tel"
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      const phone = document.getElementById('waiver-whatsapp').value;
                      if (!phone) {
                        toast.error('Please enter a phone number');
                        return;
                      }
                      sendWaiverViaNative(waiverSendDialog.booking, waiverSendDialog.guest, 'whatsapp', phone);
                    }}
                    className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageSquare className="h-4 w-4" />
                    WhatsApp
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Opens your native app to send a personalized message with the waiver link.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiverSendDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Composer Dialog */}
      <Dialog open={!!emailDialog} onOpenChange={() => setEmailDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Email to {emailDialog?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input value={emailDialog?.email || ''} disabled className="bg-muted" />
            </div>
            
            <div>
              <Label>Subject</Label>
              <Input 
                value={emailSubject} 
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject..."
              />
            </div>
            
            <div>
              <Label>Message</Label>
              <Textarea 
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Type your message..."
                className="min-h-[200px]"
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEmailBody(prev => prev + '\n\nBest regards,\nTour Finder Team')}
              >
                Add Signature
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const booking = emailDialog?.booking;
                  if (booking) {
                    setEmailBody(`Dear ${emailDialog.name},\n\nHere are your booking details:\n\nTour: ${booking.tour_title}\nDate: ${formatDate(booking.date)}\nTime: ${formatTime12hr(booking.time)}\nGuests: ${booking.guests}\nTotal: ${formatPrice(booking.total_price)}\n\nPlease let us know if you have any questions.\n\nBest regards,\nTour Finder Team`);
                  }
                }}
              >
                Insert Booking Details
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(null)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail} className="gap-2">
              {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Composer Dialog */}
      <Dialog open={!!smsDialog} onOpenChange={() => setSmsDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Send SMS to {smsDialog?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input value={smsDialog?.phone || ''} disabled className="bg-muted" />
            </div>
            
            <div>
              <Label>Message ({smsMessage.length}/160 characters)</Label>
              <Textarea 
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your SMS message..."
                className="min-h-[100px]"
                maxLength={320}
              />
              <p className="text-xs text-muted-foreground mt-1">
                SMS longer than 160 characters may be split into multiple messages
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const booking = smsDialog?.booking;
                  if (booking) {
                    setSmsMessage(`Reminder: Your ${booking.tour_title} is on ${formatDate(booking.date)} at ${formatTime12hr(booking.time)}. See you there!`);
                  }
                }}
              >
                Reminder Template
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsDialog(null)}>Cancel</Button>
            <Button onClick={handleSendSms} disabled={sendingSms} className="gap-2">
              {sendingSms ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={!!refundDialog} onOpenChange={() => setRefundDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-600" />
              Process Refund
            </DialogTitle>
          </DialogHeader>
          
          {refundDialog && (
            <div className="space-y-4">
              {/* Booking Summary */}
              <Card className="p-4 bg-muted/50">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Booking:</span>
                    <span className="font-medium">{refundDialog.tour_title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer:</span>
                    <span className="font-medium">{refundDialog.booker_name || refundDialog.customer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Original Amount:</span>
                    <span className="font-bold text-lg">{formatPrice(refundDialog.total_price)}</span>
                  </div>
                  {refundDialog.stripe_session_id && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment ID:</span>
                      <span className="font-mono text-xs">{refundDialog.stripe_session_id.substring(0, 20)}...</span>
                    </div>
                  )}
                </div>
              </Card>
              
              {/* Refund Type */}
              <div>
                <Label>Refund Type</Label>
                <Select value={refundType} onValueChange={(v) => {
                  setRefundType(v);
                  if (v === 'full') {
                    setRefundAmount(refundDialog.total_price?.toString() || '0');
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Refund</SelectItem>
                    <SelectItem value="partial">Partial Refund</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Refund Amount */}
              <div>
                <Label>Refund Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="pl-8"
                    disabled={refundType === 'full'}
                    step="0.01"
                    max={refundDialog.total_price}
                  />
                </div>
                {refundType === 'partial' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Maximum refund: {formatPrice(refundDialog.total_price)}
                  </p>
                )}
              </div>
              
              {/* Refund Reason */}
              <div>
                <Label>Refund Reason</Label>
                <Select value={refundReason} onValueChange={setRefundReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_request">Customer Request</SelectItem>
                    <SelectItem value="tour_cancelled">Tour Cancelled</SelectItem>
                    <SelectItem value="weather">Weather Conditions</SelectItem>
                    <SelectItem value="duplicate_booking">Duplicate Booking</SelectItem>
                    <SelectItem value="service_issue">Service Issue</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Admin Notes */}
              <div>
                <Label>Admin Notes (Internal - not sent to customer)</Label>
                <Textarea 
                  value={refundNotes}
                  onChange={(e) => setRefundNotes(e.target.value)}
                  placeholder="Add notes about this refund for internal records..."
                  className="min-h-[80px]"
                />
              </div>
              
              {/* Send Email Option */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <input 
                  type="checkbox" 
                  id="send-refund-email"
                  checked={sendRefundEmail}
                  onChange={(e) => setSendRefundEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="send-refund-email" className="text-sm flex-1">
                  <span className="font-medium">Send refund confirmation email</span>
                  <p className="text-xs text-muted-foreground">
                    Customer will receive an email confirming the refund with details
                  </p>
                </label>
              </div>
              
              {/* Warning */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Refund will be processed via Stripe</p>
                    <p className="text-amber-700">
                      This action will issue a refund of {formatPrice(parseFloat(refundAmount) || 0)} to the customer&apos;s original payment method.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleProcessRefund} 
              disabled={processingRefund}
              className="gap-2 bg-amber-600 hover:bg-amber-700"
            >
              {processingRefund ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              Process Refund - {formatPrice(parseFloat(refundAmount) || 0)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Cancel Booking
            </DialogTitle>
          </DialogHeader>
          
          {cancelDialog && (
            <div className="space-y-4">
              {/* Booking Summary */}
              <Card className="p-4 bg-muted/50">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Booking:</span>
                    <span className="font-medium">{cancelDialog.tour_title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer:</span>
                    <span className="font-medium">{cancelDialog.booker_name || cancelDialog.customer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date:</span>
                    <span className="font-medium">{formatDate(cancelDialog.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-bold">{formatPrice(cancelDialog.total_price)}</span>
                  </div>
                </div>
              </Card>
              
              {/* Cancellation Reason */}
              <div>
                <Label>Cancellation Reason *</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_request">Customer Request</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                    <SelectItem value="weather">Weather Conditions</SelectItem>
                    <SelectItem value="tour_cancelled">Tour Cancelled</SelectItem>
                    <SelectItem value="operator_unavailable">Operator Unavailable</SelectItem>
                    <SelectItem value="duplicate_booking">Duplicate Booking</SelectItem>
                    <SelectItem value="payment_issue">Payment Issue</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Admin Notes */}
              <div>
                <Label>Admin Notes (Internal)</Label>
                <Textarea 
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  placeholder="Add internal notes about this cancellation..."
                  className="min-h-[80px]"
                />
              </div>
              
              {/* Send Email Option */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <input 
                  type="checkbox" 
                  id="send-cancel-email"
                  checked={sendCancelEmail}
                  onChange={(e) => setSendCancelEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="send-cancel-email" className="text-sm flex-1">
                  <span className="font-medium">Send cancellation email to customer</span>
                  <p className="text-xs text-muted-foreground">
                    Customer will receive an email notifying them of the cancellation
                  </p>
                </label>
              </div>
              
              {/* Warning */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-800">This action cannot be undone</p>
                    <p className="text-red-700">
                      The booking will be marked as cancelled. If the customer paid, you may want to process a refund separately.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(null)}>Close</Button>
            <Button 
              onClick={handleCancelBooking} 
              disabled={processingCancel || !cancelReason}
              className="gap-2 bg-red-600 hover:bg-red-700"
            >
              {processingCancel ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Waiver Reminder Dialog */}
      <Dialog open={!!waiverDialog} onOpenChange={(open) => !open && setWaiverDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Send Waiver Reminder
            </DialogTitle>
          </DialogHeader>
          {waiverDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900">{waiverDialog.tour_title}</h4>
                <p className="text-sm text-purple-700">
                  Reference: {waiverDialog.reference_number} | Date: {formatDate(waiverDialog.date)}
                </p>
              </div>

              {/* Unsigned Guests Count */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="font-semibold text-amber-900">
                  {getUnsignedGuestsCount(waiverDialog)} guest(s) still need to sign waivers
                </p>
                <div className="mt-2 space-y-1">
                  {waiverDialog.guest_info?.filter((g, i) => i > 0 && !g.waiver_signed).map((guest, idx) => (
                    <p key={idx} className="text-sm text-amber-800">
                      • {guest.full_name || `Guest ${idx + 2}`}
                    </p>
                  ))}
                </div>
              </div>

              {/* Recipient Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Send to:</p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Email:</strong> {waiverDialog.booker_email || waiverDialog.customer_email || 'Not available'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Phone:</strong> {waiverDialog.booker_phone || waiverDialog.customer_phone || 'Not available'}
                </p>
              </div>

              {/* Custom Message (optional) */}
              <div>
                <Label className="text-sm font-medium">Custom Message (optional)</Label>
                <Textarea
                  value={waiverCustomMessage}
                  onChange={(e) => setWaiverCustomMessage(e.target.value)}
                  placeholder="Add a personal message to include in the reminder..."
                  className="mt-1"
                  rows={2}
                />
              </div>

              {/* Send Buttons - Native Links */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Choose how to send:</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button 
                    onClick={() => sendWaiverViaNativeLink(waiverDialog, 'email')}
                    className="gap-2"
                    disabled={!waiverDialog.booker_email && !waiverDialog.customer_email}
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => sendWaiverViaNativeLink(waiverDialog, 'sms')}
                    className="gap-2"
                    disabled={!waiverDialog.booker_phone && !waiverDialog.customer_phone}
                  >
                    <MessageSquare className="h-4 w-4" />
                    SMS
                  </Button>
                  <Button 
                    onClick={() => sendWaiverViaNativeLink(waiverDialog, 'whatsapp')}
                    className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                    disabled={!waiverDialog.booker_phone && !waiverDialog.customer_phone}
                  >
                    <MessageSquare className="h-4 w-4" />
                    WhatsApp
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Opens your native app with the message pre-filled
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiverDialog(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward to Provider Dialog */}
      <Dialog open={!!forwardProviderDialog} onOpenChange={(open) => !open && setForwardProviderDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              Forward Booking to Provider
            </DialogTitle>
          </DialogHeader>
          {forwardProviderDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900">{forwardProviderDialog.tour_title}</h4>
                <p className="text-sm text-orange-700">
                  Customer: {forwardProviderDialog.booker_name || forwardProviderDialog.customer_name}
                </p>
                <p className="text-sm text-orange-700">
                  Date: {formatDate(forwardProviderDialog.date || forwardProviderDialog.tour_date)}
                </p>
              </div>

              <div>
                <Label className="text-base font-semibold">Select Service Provider</Label>
                <p className="text-sm text-muted-foreground mb-2">The booking details will be emailed to the selected provider</p>
                <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a provider..." />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name || provider.company_name} - {provider.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForwardProviderDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleForwardToProvider} 
              disabled={sendingToProvider || !selectedProviderId}
              className="gap-2 bg-orange-600 hover:bg-orange-700"
            >
              {sendingToProvider ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Forward to Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Provider Dialog */}
      <Dialog open={!!assignProviderDialog} onOpenChange={(open) => !open && setAssignProviderDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Assign Provider to Booking
            </DialogTitle>
          </DialogHeader>
          {assignProviderDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-bold text-blue-900">{assignProviderDialog.tour_title}</h4>
                <p className="text-sm text-blue-700">
                  Customer: {assignProviderDialog.booker_name || assignProviderDialog.customer_name}
                </p>
                <p className="text-sm text-blue-700">
                  Date: {formatDate(assignProviderDialog.date || assignProviderDialog.tour_date)} at {formatTime12hr(assignProviderDialog.time)}
                </p>
                <p className="text-sm text-blue-700">
                  Total: {formatPrice(assignProviderDialog.total_price)} ({assignProviderDialog.guests} guests)
                </p>
              </div>

              <div>
                <Label className="text-base font-semibold">Select Service Provider *</Label>
                <p className="text-sm text-muted-foreground mb-2">This booking will be added to the provider&apos;s calendar</p>
                <Select value={assignProviderId} onValueChange={setAssignProviderId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a provider..." />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name} {provider.company_name && `(${provider.company_name})`} - {provider.commission_rate || 15}% commission
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label>Notify Provider</Label>
                <Select value={assignNotifyProvider ? 'yes' : 'no'} onValueChange={(v) => setAssignNotifyProvider(v === 'yes')}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {assignNotifyProvider && (
                <div className="flex items-center justify-between">
                  <Label>Send Via</Label>
                  <Select value={assignSendVia} onValueChange={setAssignSendVia}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {assignProviderId && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-green-800">Provider Earnings Preview:</p>
                  <p className="text-lg font-bold text-green-700">
                    {formatPrice(assignProviderDialog.total_price * (1 - (providers.find(p => p.id === assignProviderId)?.commission_rate || 15) / 100))}
                  </p>
                  <p className="text-xs text-green-600">
                    ({100 - (providers.find(p => p.id === assignProviderId)?.commission_rate || 15)}% of {formatPrice(assignProviderDialog.total_price)})
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignProviderDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignProvider} 
              disabled={assigningProvider || !assignProviderId}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {assigningProvider ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
              Assign Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
      
      {/* Message Template Selector Dialog - Rendered globally regardless of view */}
      <MessageTemplateSelector
        isOpen={templateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        channel={templateSelectorChannel}
        customerData={templateSelectorData}
        onSend={() => {
          if (onRefresh) onRefresh();
        }}
      />
    </div>
  );
}
